import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { debounce } from 'lodash';

type SearchContextType = {
  searchValue: string;
  debouncedSearchValue: string;
  onSearch: (value: string) => void;
};

type SearchContextProvider = {
  children: ReactNode;
};

export const SearchContext = createContext<SearchContextType>({
  searchValue: '',
  debouncedSearchValue: '',
  onSearch: () => {},
});

export const useSearch = () => useContext(SearchContext);

export const SearchProvider: FC<SearchContextProvider> = ({ children }) => {
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);
  const [value, setValue] = useState<string>('');
  const [debouncedValue, setDebouncedValue] = useState<string>('');

  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        setDebouncedValue(searchValue);
      }, 600),
    [],
  );

  const onSearch = useCallback(
    (text: string) => {
      setValue(text);
      debouncedSearch(text);
    },
    [debouncedSearch],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (value && location.pathname !== prevPathnameRef.current) {
      setValue('');
      setDebouncedValue('');
      debouncedSearch.cancel();
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname, debouncedSearch, value]);

  return (
    <SearchContext.Provider
      value={{
        searchValue: value,
        debouncedSearchValue: debouncedValue,
        onSearch,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
